// Registered win-probability model for AI Picks (DEC-011; accountability plan §4).
// p_A = Φ((forecast_A − forecast_B) / σ). q is the probability for the filed pick.
//
// This file has a CommonJS twin at scripts/lib/win-probability.cjs for pipeline use.
// Keep the two copies identical in behavior; src/tests/winProbability.test.js checks parity
// on a grid of margins and on every filed week, and pins q for every filed matchup.
//
// Never change sigma or the bands in place. A new value is a new `wp-N` entry plus a new
// DECISIONS.md entry, and it never applies to weeks that were already issued.
//
// effectiveFromWeek: first week the model was registered before kickoff (graded prospectively).
// frozenFromWeek:    first week whose filed edition carries the issued q (`winProbability` per
//                    matchup, top-level `probabilityModel`); prediction-validation.cjs enforces it.

export const WP_MODELS = Object.freeze({
  'wp-1': Object.freeze({ sigma: 44, registeredAt: '2026-09-29', effectiveFromWeek: 4, frozenFromWeek: 5, season: 2026 }),
});

export const CURRENT_WP_MODEL_ID = 'wp-1';

const DEFAULT_SIGMA = WP_MODELS[CURRENT_WP_MODEL_ID].sigma;

// Standard normal CDF, Hart (1968) algorithm 5666 as given by G. West (2005),
// "Better approximations to cumulative normal functions", Wilmott Magazine.
// Deterministic arithmetic only; absolute error below 1e-15 on [-8, 8] against a
// double-precision erfc reference (Φ(x) = erfc(−x/√2)/2).
export function normalCdf(x) {
  if (Number.isNaN(x)) return NaN;
  const z = Math.abs(x);
  let tail;
  if (z > 37) {
    tail = 0;
  } else {
    const e = Math.exp((-z * z) / 2);
    if (z < 7.07106781186547) {
      let n = 3.52624965998911e-2 * z + 0.700383064443688;
      n = n * z + 6.37396220353165;
      n = n * z + 33.912866078383;
      n = n * z + 112.079291497871;
      n = n * z + 221.213596169931;
      n = n * z + 220.206867912376;
      let d = 8.83883476483184e-2 * z + 1.75566716318264;
      d = d * z + 16.064177579207;
      d = d * z + 86.7807322029461;
      d = d * z + 296.564248779674;
      d = d * z + 637.333633378831;
      d = d * z + 793.826512519948;
      d = d * z + 440.413735824752;
      tail = (e * n) / d;
    } else {
      let b = z + 0.65;
      b = z + 4 / b;
      b = z + 3 / b;
      b = z + 2 / b;
      b = z + 1 / b;
      tail = e / b / 2.506628274631;
    }
  }
  return x > 0 ? 1 - tail : tail;
}

// Probability that team A outscores team B, from unrounded forecast team totals.
export function probA(forecastA, forecastB, sigma = DEFAULT_SIGMA) {
  const a = Number(forecastA);
  const b = Number(forecastB);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !(sigma > 0)) return null;
  return normalCdf((a - b) / sigma);
}

// q for the filed pick (`winnerId`), from the unrounded `score` of each team in the public forecast.
export function pickProbability(matchup, teamsById, sigma = DEFAULT_SIGMA) {
  if (!matchup || !teamsById || !Array.isArray(matchup.teamIds) || matchup.teamIds.length !== 2) return null;
  const pickId = String(matchup.winnerId);
  const ids = matchup.teamIds.map(String);
  if (!ids.includes(pickId)) return null;
  const otherId = ids[0] === pickId ? ids[1] : ids[0];
  const pick = teamsById[pickId];
  const other = teamsById[otherId];
  if (!pick || !other) return null;
  return probA(pick.score, other.score, sigma);
}

// Registered bands: [.50,.55) Toss-up, [.55,.65) Lean, [.65,1] Favorite. Anything else has no band.
export function band(q) {
  if (!Number.isFinite(q) || q < 0.5 || q > 1) return null;
  if (q < 0.55) return 'Toss-up';
  if (q < 0.65) return 'Lean';
  return 'Favorite';
}

function modelOrder(season, week) {
  return Number(season) * 100 + Number(week);
}

// The registered model that governs an issue: the latest one whose (season, effectiveFromWeek)
// is at or before the issue. Null means the issue predates every registered model.
export function modelForIssue(issue) {
  if (!issue || !Number.isInteger(issue.season) || !Number.isInteger(issue.week)) return null;
  const at = modelOrder(issue.season, issue.week);
  let found = null;
  for (const [id, model] of Object.entries(WP_MODELS)) {
    const from = modelOrder(model.season, model.effectiveFromWeek);
    if (from <= at && (!found || from >= modelOrder(found.season, found.effectiveFromWeek))) found = { id, ...model };
  }
  return found;
}

// The registered model whose q is frozen into this issue's filed edition (2026: Week 5 on), else null.
// Earlier issues must not carry `winProbability` or `probabilityModel`.
export function frozenModelForIssue(issue) {
  const model = modelForIssue(issue);
  if (!model || !Number.isInteger(model.frozenFromWeek)) return null;
  return modelOrder(issue.season, issue.week) >= modelOrder(model.season, model.frozenFromWeek) ? model : null;
}

// q as issued: the frozen `winProbability` when the edition carries one (validated against the
// recomputation when filed), otherwise recomputed from the unrounded team scores.
export function issuedPickProbability(matchup, teamsById) {
  if (matchup && typeof matchup.winProbability === 'number' && Number.isFinite(matchup.winProbability)) return matchup.winProbability;
  return pickProbability(matchup, teamsById);
}

// True when a registered model existed before this issue's games (2026: Week 4 on).
export function isProspective(issue) {
  return modelForIssue(issue) !== null;
}

// Whole percent, rounded down, so the shown number never crosses a band edge
// (0.5466 shows 54%, still a Toss-up). Grading always uses the unrounded q.
export function wholePercent(q) {
  if (!Number.isFinite(q)) return null;
  return Math.min(99, Math.floor(q * 100 + 1e-9));
}

export function formatPickProbability(q) {
  const label = band(q);
  if (!label) return null;
  return `${wholePercent(q)}% · ${label}`;
}
